import React, { useCallback, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Drawer } from "vaul";
import {
  CalendarDays,
  Contact,
  Landmark,
  Link2,
  MoreHorizontal,
  Mic2,
  Plus,
  ScanLine,
  Ticket,
  Users,
  UserRound,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { useAuth } from "../../context/AuthContext";
import { appNav } from "../../routes/appNavigation";
import { resolveActiveEventId } from "../../routes/workspaceNavigation";
import { avatarInitials } from "../../utils/avatarInitials";
import "./MobileTabBar.css";

type TabItem = {
  key: string;
  label: string;
  Icon: LucideIcon;
  to?: string;
  end?: boolean;
  /** Rendered larger and in brand colour — the day-of-show action. */
  primary?: boolean;
};

/**
 * Five destinations, which is the practical ceiling before targets get too
 * narrow to hit. Everything else lives behind "More" rather than being
 * squeezed in.
 */
const TABS: TabItem[] = [
  { key: "calendar", label: "Calendar", Icon: CalendarDays, to: appNav.calendar(), end: true },
  { key: "events", label: "Events", Icon: Ticket, to: appNav.events() },
  { key: "scan", label: "Scan", Icon: ScanLine, primary: true },
  { key: "guests", label: "Guests", Icon: Users, to: appNav.guests() },
  { key: "more", label: "More", Icon: MoreHorizontal },
];

const MORE_LINKS: { key: string; to: string; label: string; Icon: LucideIcon }[] = [
  { key: "finance", to: appNav.finance(), label: "Finance", Icon: Wallet },
  { key: "artists", to: appNav.artists(), label: "Artists", Icon: Mic2 },
  { key: "venues", to: appNav.venues(), label: "Venues", Icon: Landmark },
  { key: "contacts", to: appNav.contacts(), label: "Contacts", Icon: Contact },
];

export const MobileTabBar: React.FC = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  /**
   * Scanning always belongs to a specific event. The tab follows the event the
   * user is already in, falling back to the last one they opened; with no
   * event at all it sends them to the list to choose rather than dead-ending.
   */
  const scanTarget = useMemo(() => {
    const eventId = resolveActiveEventId(pathname, APP_WORKSPACE);
    return eventId ? appNav.eventLive(eventId) : appNav.events();
  }, [pathname]);

  const scanIsActive = /^\/events\/[^/]+\/live$/.test(pathname);

  const closeMore = useCallback(() => setMoreOpen(false), []);

  const go = useCallback(
    (to: string) => {
      closeMore();
      navigate(to);
    },
    [closeMore, navigate]
  );

  return (
    <>
      <nav className="mobile-tabbar" aria-label="Primary">
        {TABS.map((tab) => {
          if (tab.key === "scan") {
            return (
              <button
                key={tab.key}
                type="button"
                className={`mobile-tabbar__item mobile-tabbar__item--primary${scanIsActive ? " is-active" : ""}`}
                onClick={() => navigate(scanTarget)}
                aria-current={scanIsActive ? "page" : undefined}
              >
                <span className="mobile-tabbar__primary-disc" aria-hidden>
                  <tab.Icon size={22} strokeWidth={2.2} />
                </span>
                <span className="mobile-tabbar__label">{tab.label}</span>
              </button>
            );
          }

          if (tab.key === "more") {
            return (
              <button
                key={tab.key}
                type="button"
                className={`mobile-tabbar__item${moreOpen ? " is-active" : ""}`}
                onClick={() => setMoreOpen(true)}
                aria-expanded={moreOpen}
                aria-haspopup="dialog"
              >
                <tab.Icon className="mobile-tabbar__icon" size={21} strokeWidth={2} aria-hidden />
                <span className="mobile-tabbar__label">{tab.label}</span>
              </button>
            );
          }

          return (
            <NavLink
              key={tab.key}
              to={tab.to as string}
              end={tab.end}
              className={({ isActive }) => {
                // `/events/:id/live` sits under the Events prefix, but while
                // scanning that tab owns the highlight — two lit tabs would
                // misreport where the user is.
                const active = isActive && !(tab.key === "events" && scanIsActive);
                return `mobile-tabbar__item${active ? " is-active" : ""}`;
              }}
            >
              <tab.Icon className="mobile-tabbar__icon" size={21} strokeWidth={2} aria-hidden />
              <span className="mobile-tabbar__label">{tab.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <Drawer.Root open={moreOpen} onOpenChange={setMoreOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="mobile-sheet__overlay" />
          <Drawer.Content className="mobile-sheet" aria-label="More destinations">
            <div className="mobile-sheet__grabber" aria-hidden />
            <Drawer.Title className="mobile-sheet__title">More</Drawer.Title>

            <div className="mobile-sheet__links">
              {MORE_LINKS.map((link) => (
                <button
                  key={link.key}
                  type="button"
                  className="mobile-sheet__link"
                  onClick={() => go(link.to)}
                >
                  <link.Icon className="mobile-sheet__link-icon" size={20} strokeWidth={2} aria-hidden />
                  <span>{link.label}</span>
                </button>
              ))}

              {user?.is_system_admin ? (
                <button
                  type="button"
                  className="mobile-sheet__link"
                  onClick={() => go(appNav.ticketingLinks())}
                >
                  <Link2 className="mobile-sheet__link-icon" size={20} strokeWidth={2} aria-hidden />
                  <span>zoniq.in links</span>
                </button>
              ) : null}
            </div>

            <div className="mobile-sheet__footer">
              <button
                type="button"
                className="mobile-sheet__cta"
                onClick={() => go(appNav.newEvent())}
              >
                <Plus size={19} strokeWidth={2.4} aria-hidden />
                <span>Add Event</span>
              </button>

              <button
                type="button"
                className="mobile-sheet__profile"
                onClick={() => go(appNav.profile())}
              >
                {user ? (
                  <span className="mobile-sheet__avatar" aria-hidden>
                    {avatarInitials(user)}
                  </span>
                ) : (
                  <UserRound size={20} strokeWidth={2} aria-hidden />
                )}
                <span className="mobile-sheet__profile-name">{user?.full_name ?? "Profile"}</span>
              </button>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
};
